import React, { useState } from "react";
import { Pill } from "../../../components/ui/Pill";

// === Types (mirror backend/models/Brand.ts — keep in sync) ===
export type AccountType = "domaine" | "resto";

export interface FeederDomaine {
  philosophie: string[];
  vinification: string[];
  elevage: string[];
  style: string[];
  instructions?: string;
}
export interface FeederResto {
  menu: string;
  instructions?: string;
}
export interface Feeder {
  accountType: AccountType;
  domaine: FeederDomaine;
  resto: FeederResto;
}

type DomaineToggleGroup = "philosophie" | "vinification" | "elevage" | "style";

export const DEFAULT_FEEDER: Feeder = {
  accountType: "domaine",
  domaine: { philosophie: [], vinification: [], elevage: [], style: [] },
  resto: { menu: "" },
};

// === Button catalogues (visible labels & emoji hints for users) ===
interface FBtn { id: string; label: string; hint: string; }

const PHILO: FBtn[] = [
  { id: "bio",        label: "Bio / Conversion",     hint: "Sols vivants, expression du terroir" },
  { id: "biodynamie", label: "Biodynamie",            hint: "Calendrier lunaire, énergie du vin" },
  { id: "nature",     label: "Vin Nature",            hint: "Sans soufre, vin vivant" },
];
const VINIF: FBtn[] = [
  { id: "vendange-entiere",  label: "Vendange entière",   hint: "Avec rafle — florales, épicé" },
  { id: "egrappage",         label: "100% égrappé",        hint: "Fruit éclatant, soyeux" },
  { id: "levures-indigenes", label: "Levures indigènes",   hint: "Singularité, sauvage" },
];
const ELEVAGE: FBtn[] = [
  { id: "inox",          label: "Cuve inox",            hint: "Tendu, croquant, sans bois" },
  { id: "fut-neuf",      label: "Fûts neufs",            hint: "Vanille, toasté, garde" },
  { id: "fut-ancien",    label: "Fûts anciens / foudres", hint: "Patinés, élégance" },
  { id: "beton-amphore", label: "Béton / amphore",       hint: "Pureté, minéral" },
];
const STYLE: FBtn[] = [
  { id: "soif",        label: "Vin de soif",       hint: "Apéro, fruit, convivial" },
  { id: "gastronomie", label: "Vin de gastronomie", hint: "Plat en sauce, élégant" },
  { id: "garde",       label: "Vin de garde",       hint: "Grand cru, patience" },
];

interface SectionProps {
  title: string;
  subtitle: string;
  emoji: string;
  options: FBtn[];
  selected: string[];
  onToggle: (id: string) => void;
}

function Section({ title, subtitle, emoji, options, selected, onToggle }: SectionProps) {
  return (
    <div className="card p-4 mb-3">
      <div className="flex items-baseline gap-2 mb-1">
        <span className="text-base leading-none">{emoji}</span>
        <h3 className="text-[13px] font-medium tracking-wide text-foreground m-0">{title}</h3>
      </div>
      <div className="text-[11px] text-tertiary mb-3 leading-snug">{subtitle}</div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {options.map((o) => {
          const active = selected.includes(o.id);
          return (
            <button
              key={o.id}
              type="button"
              onClick={() => onToggle(o.id)}
              className={`text-left p-2.5 rounded-[7px] border transition-all duration-150 cursor-pointer ${
                active
                  ? "bg-accent-bg border-accent-border text-accent shadow-[0_0_0_1px_rgba(196,154,60,0.10)]"
                  : "bg-transparent border-border text-secondary hover:border-foreground/20 hover:text-foreground"
              }`}
            >
              <div className="flex items-center gap-1.5 text-[12px] font-medium leading-tight">
                <span className={`inline-block w-2 h-2 rounded-full ${active ? "bg-accent" : "bg-foreground/15"}`} />
                {o.label}
              </div>
              <div className={`text-[10px] mt-1 leading-snug ${active ? "text-accent/80" : "text-tertiary"}`}>{o.hint}</div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

interface FeederPanelProps {
  feeder: Feeder;
  onChange: (f: Feeder) => void;
}

export function FeederPanel({ feeder, onChange }: FeederPanelProps) {
  const [saved, setSaved] = useState(false);

  const toggle = (group: DomaineToggleGroup) => (id: string) => {
    const cur = feeder.domaine[group];
    const next = cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id];
    onChange({ ...feeder, domaine: { ...feeder.domaine, [group]: next } });
    flash();
  };

  const setMenu = (menu: string) => {
    onChange({ ...feeder, resto: { ...feeder.resto, menu } });
    flash();
  };

  const setDomaineInstructions = (instructions: string) => {
    onChange({ ...feeder, domaine: { ...feeder.domaine, instructions } });
    flash();
  };

  const setRestoInstructions = (instructions: string) => {
    onChange({ ...feeder, resto: { ...feeder.resto, instructions } });
    flash();
  };

  const setAccount = (t: AccountType) => {
    onChange({ ...feeder, accountType: t });
    flash();
  };

  const flash = () => {
    setSaved(true);
    window.clearTimeout((flash as any)._t);
    (flash as any)._t = window.setTimeout(() => setSaved(false), 1200);
  };

  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const txt = await f.text();
    setMenu(txt.slice(0, 4000));
    e.target.value = "";
  };

  const totalDomaineSelected =
    feeder.domaine.philosophie.length +
    feeder.domaine.vinification.length +
    feeder.domaine.elevage.length +
    feeder.domaine.style.length;

  return (
    <div className="max-w-[720px] mx-auto px-5 pt-5 pb-12 w-full">
      {/* === Profile switcher === */}
      <div className="card p-4 mb-4">
        <div className="text-[10px] tracking-[2px] uppercase text-tertiary mb-2">Profil utilisateur</div>
        <div className="grid grid-cols-2 gap-2">
          {[
            { id: "domaine" as AccountType, label: "Domaine / Vigneron", sub: "Philosophie, vinif, élevage" },
            { id: "resto" as AccountType,   label: "Restaurant / Caviste", sub: "Carte du chef" },
          ].map((opt) => {
            const active = feeder.accountType === opt.id;
            return (
              <button
                key={opt.id}
                onClick={() => setAccount(opt.id)}
                className={`p-3 rounded-[7px] border text-left cursor-pointer transition-all ${
                  active
                    ? "bg-accent-bg border-accent-border text-accent"
                    : "bg-transparent border-border text-secondary hover:border-foreground/20"
                }`}
              >
                <div className="text-[13px] font-medium">{opt.label}</div>
                <div className={`text-[11px] mt-0.5 ${active ? "text-accent/80" : "text-tertiary"}`}>{opt.sub}</div>
              </button>
            );
          })}
        </div>
        <div className={`text-[10px] mt-2.5 transition-opacity ${saved ? "opacity-100 text-accent" : "opacity-0"}`}>
          ✓ Préférences enregistrées
        </div>
      </div>

      {/* === Domaine feeder === */}
      {feeder.accountType === "domaine" && (
        <>
          <div className="mb-3 flex items-baseline justify-between">
            <div>
              <div className="text-[10px] tracking-[2px] uppercase text-tertiary">Feeder Domaine</div>
              <div className="text-[12px] text-secondary mt-0.5">
                Configure l'ADN de tes vins une fois — l'IA s'en sert pour CHAQUE génération.
              </div>
            </div>
            <span className="text-[10px] px-2 py-0.5 rounded-md bg-gold-bg border border-gold/20 text-gold">
              {totalDomaineSelected} sélection{totalDomaineSelected > 1 ? "s" : ""}
            </span>
          </div>

          <Section
            title="Vigne & Philosophie"
            subtitle="L'ADN éthique et agricole du domaine — utilisé pour l'argumentaire de vente."
            emoji="🌿"
            options={PHILO}
            selected={feeder.domaine.philosophie}
            onToggle={toggle("philosophie")}
          />
          <Section
            title="Vinification"
            subtitle="Choix qui sculptent la texture en bouche — crucial en Bourgogne, Rhône."
            emoji="🍇"
            options={VINIF}
            selected={feeder.domaine.vinification}
            onToggle={toggle("vinification")}
          />
          <Section
            title="Élevage"
            subtitle="Ce qui façonne le vin pendant des mois — l'info la plus demandée par les amateurs."
            emoji="🛢️"
            options={ELEVAGE}
            selected={feeder.domaine.elevage}
            onToggle={toggle("elevage")}
          />
          <Section
            title="Style & Usage"
            subtitle="Raccourcis commerciaux — utile surtout pour les cavistes / restaurateurs."
            emoji="🎯"
            options={STYLE}
            selected={feeder.domaine.style}
            onToggle={toggle("style")}
          />

          <div className="card p-4 mb-3">
            <label className="label">Instructions personnalisées</label>
            <div className="text-[11px] text-tertiary mb-2 leading-snug">
              Consignes libres appliquées à CHAQUE génération (ton, mentions obligatoires, mots à éviter…).
            </div>
            <textarea
              value={feeder.domaine.instructions || ""}
              onChange={(e) => setDomaineInstructions(e.target.value)}
              placeholder="Ex : Toujours mentionner notre certification bio. Tutoiement, ton chaleureux. Ne jamais écrire « fruité »."
              rows={4}
              maxLength={1500}
              className="w-full bg-foreground/[0.02] border border-border rounded-[7px] p-3 text-[12px] text-foreground"
            />
          </div>

          <div className="text-[10px] text-tertiary italic mt-2 leading-relaxed">
            Tu peux cocher zéro, un ou plusieurs boutons par bloc. Plus tu en sélectionnes, plus l'IA personnalise la fiche.
          </div>
        </>
      )}

      {/* === Resto feeder === */}
      {feeder.accountType === "resto" && (
        <>
          <div className="mb-3">
            <div className="text-[10px] tracking-[2px] uppercase text-tertiary">Feeder Carte du Chef</div>
            <div className="text-[12px] text-secondary mt-0.5">
              Colle ta carte une fois, l'IA scanne le menu pour suggérer le bon plat avec chaque vin.
            </div>
          </div>

          <div className="card p-4 mb-3">
            <div className="flex items-center justify-between mb-2">
              <label className="label !mb-0">Votre Menu Actuel</label>
              <div className="flex gap-2 items-center">
                <label className="px-3 py-1.5 rounded-[5px] text-[11px] border border-border text-secondary cursor-pointer hover:border-foreground/20 transition-colors">
                  Importer .txt
                  <input type="file" accept=".txt,.md,.csv" onChange={onFile} className="hidden" />
                </label>
                {feeder.resto.menu && (
                  <button
                    onClick={() => setMenu("")}
                    className="text-[11px] text-error bg-transparent border-none cursor-pointer"
                  >
                    Vider
                  </button>
                )}
              </div>
            </div>
            <textarea
              value={feeder.resto.menu}
              onChange={(e) => setMenu(e.target.value)}
              placeholder={`Entrées\n— Tartare de bœuf, mayonnaise wasabi\n— Velouté de potimarron, châtaignes torréfiées\n\nPlats\n— Bar de ligne, sauce vierge, fenouil confit\n— Côte de bœuf maturée 30j, frites maison\n\nFromages — sélection affineur\n\nDesserts\n— Soufflé Grand Marnier\n— Tarte fine aux figues, glace miel`}
              rows={12}
              className="w-full bg-foreground/[0.02] border border-border rounded-[7px] p-3 text-[12px] text-foreground font-mono leading-relaxed outline-none focus:border-accent-border resize-y"
            />
            <div className="flex items-center justify-between mt-2">
              <div className="text-[10px] text-tertiary">{feeder.resto.menu.length} caractères · ne pas dépasser ~4 000 pour des résultats optimaux</div>
              <div className={`text-[10px] transition-opacity ${saved ? "opacity-100 text-accent" : "opacity-0"}`}>
                ✓ Enregistré
              </div>
            </div>
          </div>

          <div className="card p-4 mb-3">
            <label className="label">Instructions personnalisées</label>
            <div className="text-[11px] text-tertiary mb-2 leading-snug">
              Consignes libres appliquées à CHAQUE génération (ton, plats à mettre en avant, mentions obligatoires…).
            </div>
            <textarea
              value={feeder.resto.instructions || ""}
              onChange={(e) => setRestoInstructions(e.target.value)}
              placeholder="Ex : Mettre en avant les accords avec nos plats signature. Ton gastronomique mais accessible."
              rows={4}
              maxLength={1500}
              className="w-full bg-foreground/[0.02] border border-border rounded-[7px] p-3 text-[12px] text-foreground"
            />
          </div>

          <div className="card p-3.5 bg-foreground/[0.02]">
            <div className="text-[10px] tracking-[2px] uppercase text-tertiary mb-1.5">Workflow invisible</div>
            <ol className="text-[11px] text-secondary leading-relaxed list-decimal pl-4 space-y-0.5 m-0">
              <li>Tu importes tes vins (CSV ou manuellement).</li>
              <li>Tu cliques sur « Générer toutes les fiches ».</li>
              <li>L'IA lit chaque vin et scanne automatiquement ta carte.</li>
              <li>Elle déduit l'accord et nomme le plat exact dans le texte.</li>
            </ol>
          </div>
        </>
      )}
    </div>
  );
}
